import * as React from 'react';
import { Inbox, AlertTriangle } from 'lucide-react';
import './StatePanel.css';

/**
 * StatePanel (CC8): the one shape for empty, no-result and failed states.
 *
 *   <StatePanel icon={<Mic />} title="You have no recordings yet"
 *               description="Start a simulation to see your practice sessions here."
 *               action={<Button>Go to Simulations</Button>} />
 *
 *   <ErrorCard title="Couldn't load scenarios"
 *              description="Something went wrong. Please try again."
 *              action={<Button>Refresh page</Button>} />
 *
 * Props
 *   icon         a lucide element (rendered stroke-only in a teal-50 tile;
 *                amber tile for the error variant). Defaults per variant.
 *   title        display title; omit it and only the description renders,
 *                which is how a page passes an existing message string
 *                through unchanged.
 *   description  muted body copy (string or nodes).
 *   action       one or more Buttons; renders in the action row.
 *   layout       'card' (default, Card chrome, min-height 260),
 *                'compact' (tile radius, shorter), 'inline' (icon, text and
 *                action on one row - for a banner above content),
 *                'plain' (no chrome - inside a panel that already has one).
 *   variant      'empty' | 'error' (ErrorCard sets this for you).
 *   as           heading level for the title ('h3' by default; standalone
 *                state pages pass 'h1' so the page keeps its own h1).
 *   role         forwarded; ErrorCard defaults to role="alert".
 *
 * `EmptyState` is an alias of StatePanel for readability at call sites.
 */
export type StatePanelLayout = 'card' | 'compact' | 'inline' | 'plain';

export interface StatePanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  layout?: StatePanelLayout;
  variant?: 'empty' | 'error';
  /** Heading level for the title; default 'h3'. */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'p' | 'div';
  /** Hide the icon tile entirely (e.g. inside a very small panel). */
  hideIcon?: boolean;
  children?: React.ReactNode;
}

export function StatePanel({
  icon,
  title,
  description,
  action,
  layout = 'card',
  variant = 'empty',
  as: TitleTag = 'h3',
  hideIcon = false,
  className = '',
  children,
  ...rest
}: StatePanelProps) {
  const cls = [
    'lnr-state',
    `lnr-state--${variant}`,
    layout !== 'card' ? `lnr-state--${layout}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  // lnr-icon keeps the glyph stroke-only under the legacy dark-mode svg fill block.
  const defaultIcon =
    variant === 'error' ? (
      <AlertTriangle className="lnr-icon" aria-hidden="true" />
    ) : (
      <Inbox className="lnr-icon" aria-hidden="true" />
    );
  return (
    <div className={cls} {...rest}>
      {!hideIcon && (
        <span className="lnr-state__icon" aria-hidden="true">
          {icon ?? defaultIcon}
        </span>
      )}
      <div className="lnr-state__body">
        {title ? <TitleTag className="lnr-state__title">{title}</TitleTag> : null}
        {description ? <div className="lnr-state__desc">{description}</div> : null}
        {children}
      </div>
      {action ? <div className="lnr-state__actions">{action}</div> : null}
    </div>
  );
}

export const EmptyState = StatePanel;

/** StatePanel with the amber accent and role="alert"; same props. */
export function ErrorCard({ role = 'alert', ...props }: StatePanelProps) {
  return <StatePanel variant="error" role={role} {...props} />;
}

export default StatePanel;
